import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { SnackbarProvider } from './contexts/SnackbarContext.tsx';
import Login from './components/Login.tsx';
import Layout from './components/Layout.tsx';
import Dashboard from './components/Dashboard.tsx';
import EmployeesPage from './pages/EmployeesPage.tsx';
import TipOutLogForm from './pages/TipOutLogForm.tsx';
import TipOutLogForms from './pages/TipOutLogForms.tsx';
import LotteryPage from './pages/LotteryPage.tsx';
import LotteryPurchasePage from './pages/LotteryPurchasePage.tsx';
import CashDepositsPage from './pages/CashDepositsPage.tsx';
import TipsPaidCashOutsPage from './pages/TipsPaidCashOutsPage.tsx';
import MiscPurchasesPage from './pages/MiscPurchasesPage.tsx';
import DonationsPage from './pages/DonationsPage.tsx';
import VacationPage from './pages/VacationPage.tsx';
import SickFormPage from './pages/SickFormPage.tsx';
import NewBalancePage from './pages/NewBalancePage.tsx';
import NewBalanceFormsPage from './pages/NewBalanceFormsPage.tsx';
import DailySpecialsPage from './pages/DailySpecialsPage.tsx';
import EventReservationsPage from './pages/EventReservationsPage.tsx';
import WorkWithUsPage from './pages/WorkWithUsPage.tsx';
import SickLeaveSubmissionsPage from './pages/SickLeaveSubmissionsPage.tsx';
import EventFormPage from './pages/EventFormPage.tsx';
import EventFormsListPage from './pages/EventFormsListPage.tsx';
import PerformanceEventFormPage from './pages/PerformanceEventFormPage.tsx';
import PerformanceEventsListPage from './pages/PerformanceEventsListPage.tsx';
import ReservationFormPage from './pages/ReservationFormPage.tsx';
import ReservationFormsListPage from './pages/ReservationFormsListPage.tsx';
import ThanksgivingReservationsPage from './pages/ThanksgivingReservationsPage.tsx';
import ForgotPassword from './pages/ForgotPassword.tsx';
import ResetPassword from './pages/ResetPassword.tsx';
import VendorsPage from './pages/VendorsPage.tsx';
import OpenTableReservationsPage from './pages/OpenTableReservationsPage.tsx';
import OpenTableImportsPage from './pages/OpenTableImportsPage.tsx';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const isAdmin = user?.type === 'admin';

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login />
            )
          }
        />
        <Route
          path="/forgot-password"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ForgotPassword />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <ResetPassword />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <Layout>
                <Dashboard />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/employees"
          element={
            isAuthenticated ? (
              <Layout>
                <EmployeesPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tip-out-log"
          element={
            isAuthenticated ? (
              <Layout>
                <TipOutLogForm />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tip-out-log-forms"
          element={
            isAuthenticated ? (
              <Layout>
                <TipOutLogForms />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/lottery"
          element={
            isAuthenticated ? (
              <Layout>
                <LotteryPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/lottery-purchase"
          element={
            isAuthenticated ? (
              <Layout>
                <LotteryPurchasePage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/cash-deposits"
          element={
            isAuthenticated ? (
              <Layout>
                <CashDepositsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/sick-form"
          element={
            isAuthenticated ? (
              <Layout>
                <SickFormPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/vacation"
          element={
            isAuthenticated ? (
              <Layout>
                <VacationPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/misc-purchases"
          element={
            isAuthenticated ? (
              <Layout>
                <MiscPurchasesPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/donations"
          element={
            isAuthenticated ? (
              <Layout>
                <DonationsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/new-balance"
          element={
            isAuthenticated ? (
              <Layout>
                <NewBalancePage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/new-balance-forms"
          element={
            isAuthenticated ? (
              <Layout>
                <NewBalanceFormsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/tips-paid-cash-outs"
          element={
            isAuthenticated ? (
              <Layout>
                <TipsPaidCashOutsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/daily-specials"
          element={
            isAuthenticated ? (
              <Layout>
                <DailySpecialsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/event-reservations"
          element={
            isAuthenticated ? (
              <Layout>
                <EventReservationsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/open-table-reservations"
          element={
            isAuthenticated ? (
              <Layout>
                <OpenTableReservationsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/work-with-us"
          element={
            isAuthenticated ? (
              <Layout>
                <WorkWithUsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/sick-leave-submissions"
          element={
            isAuthenticated ? (
              <Layout>
                <SickLeaveSubmissionsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/event-form"
          element={
            isAuthenticated ? (
              <Layout>
                <EventFormPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/event-forms-list"
          element={
            isAuthenticated ? (
              <Layout>
                <EventFormsListPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/performance-event"
          element={
            isAuthenticated ? (
              <Layout>
                <PerformanceEventFormPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/performance-events-list"
          element={
            isAuthenticated ? (
              <Layout>
                <PerformanceEventsListPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/reservation-form"
          element={
            isAuthenticated ? (
              <Layout>
                <ReservationFormPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/reservation-forms-list"
          element={
            isAuthenticated ? (
              <Layout>
                <ReservationFormsListPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/thanksgiving-reservations"
          element={
            isAuthenticated ? (
              <Layout>
                <ThanksgivingReservationsPage />
              </Layout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/vendors"
          element={
            isAuthenticated ? (
              isAdmin ? (
                <Layout>
                  <VendorsPage />
                </Layout>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/opentable-imports"
          element={
            isAuthenticated ? (
              isAdmin ? (
                <Layout>
                  <OpenTableImportsPage />
                </Layout>
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />}
        />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;